import React, { useState, useContext, useEffect, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from "../../utils/AuthContext";
import { enqueueSnackbar } from 'notistack';
import { BsStripe } from "react-icons/bs";
import { formatDate } from '../../utils/utilFuncs';
import { IoIosCloseCircleOutline } from 'react-icons/io';
import { SiWise } from "react-icons/si";
import { FaPaypal } from "react-icons/fa";
import { Switch } from '@mui/material';
import { hostNameBack } from '../../utils/constants';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Loader from '../../utils/Loader';

const SellerEarnings = () => {
    const [loading, setLoading] = useState(false);
    const [showWithdrawalModel, setShowWithdrawalModel] = useState(false);
    const [showAddWiseModel, setShowAddWiseModel] = useState(false);
    const [showAddPaypalModel, setShowAddPaypalModel] = useState(false);
    const [wiseAccountName, setWiseAccountName] = useState("");
    const [wiseAccountEmail, setWiseAccountEmail] = useState("");
    const [paypalEmail, setPaypalEmail] = useState("");
    const [stripeActive, setStripeActive] = useState(false);
    const [wiseActive, setWiseActive] = useState(false);
    const [paypalActive, setPaypalActive] = useState(false);
    const { user, fetchUserData } = useContext(AuthContext);
    const [restrictions, setRestrictions] = useState([]);

    const [history, setHistory] = useState([]);
    const [earnings, setEarnings] = useState({
        availableBalance: 0,
        totalEarnings: 0,
        paidBalance: 0,
        requestedForWithdrawal: 0,
        productsSold: 0,
        servicesDone: 0
    });

    const [filter, setFilter] = useState("1m");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const token = localStorage.getItem("token");
    const today = new Date().toISOString().split("T")[0];

    const [stripeAccountStatus, setStripeAccountStatus] = useState(null);

    const [enabledMethods, setEnabledMethods] = useState({
        stripe: true,
        wise: true,
        paypal: true
    });

    useEffect(() => {
        if (localStorage.getItem('requestSubmitted')) {
            enqueueSnackbar("Withdrawal request submitted!", { variant: "success" });
            localStorage.removeItem('requestSubmitted');
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [methodsResponse, restrictionsResponse] = await Promise.all([
                    axios.get(`${hostNameBack}/api/v1/settings/admin/keys`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${hostNameBack}/api/v1/settings/admin/seller/restrictions`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);
                if (methodsResponse.data.success) {
                    setEnabledMethods(methodsResponse.data.paymentMethods || { stripe: true, wise: true, paypal: true });
                }
                if (restrictionsResponse.data.success) {
                    setRestrictions(restrictionsResponse.data.restrictions || []);
                }
            } catch (error) {
                console.error("Error fetching payment data:", error);
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [token]);

    useEffect(() => {
        if (!user) return;
        const activeMethod = user?.sellerId?.activePaymentMethod;
        if (activeMethod && !isMethodAvailable(activeMethod)) {
            setStripeActive(false);
            setWiseActive(false);
            setPaypalActive(false);
            enqueueSnackbar(`The payment method ${activeMethod} is no longer available.`, { variant: "info" });
            updateActivePaymentMethod(null);
        } else {
            setStripeActive(activeMethod === "Stripe");
            setWiseActive(activeMethod === "Wise");
            setPaypalActive(activeMethod === "Paypal");
        }
        if (user?.sellerId?.wiseAccountName) setWiseAccountName(user?.sellerId?.wiseAccountName);
        if (user?.sellerId?.wiseAccountEmail) setWiseAccountEmail(user?.sellerId?.wiseAccountEmail);
        if (user?.sellerId?.paypalEmail) setPaypalEmail(user?.sellerId?.paypalEmail);

        axios.get(`${hostNameBack}/api/v1/payments/seller/${user?.sellerId?._id}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(response => {
                if (response.data.success) setHistory(response.data.history);
            })
            .catch(() => enqueueSnackbar("Something went wrong with payments", { variant: 'error' }));

        axios.get(`${hostNameBack}/api/v1/payments/seller/${user?.sellerId?._id}/earnings`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(response => {
                if (response.data.success) setEarnings(response.data.earnings);
            })
    }, [user, token, enabledMethods, restrictions]);

    useEffect(() => {
        if (showAddWiseModel && (user?.sellerId?.wiseAccountName || user?.sellerId?.wiseAccountEmail)) {
            setWiseAccountName(user.sellerId.wiseAccountName);
            setWiseAccountEmail(user.sellerId.wiseAccountEmail);
        }
    }, [showAddWiseModel, user]);

    useEffect(() => {
        if (showAddPaypalModel && user?.sellerId?.paypalEmail) {
            setPaypalEmail(user.sellerId.paypalEmail);
        }
    }, [showAddPaypalModel, user]);

    useEffect(() => {
        const fetchStripeAccountStatus = async () => {
            if (user?.sellerId?.stripeAccountId) {
                try {
                    const response = await axios.get(
                        `${hostNameBack}/api/v1/payments/seller/stripe-account-status?sellerId=${user?.sellerId?._id}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setStripeAccountStatus(response.data.status);
                } catch (error) {
                    console.error("Failed to fetch Stripe account status", error);
                }
            }
        };
        fetchStripeAccountStatus();
    }, [user, token]);

    const handleRemoveStripeAccount = async () => {
        if (window.confirm("Are you sure you want to remove the incomplete Stripe account?")) {
            try {
                await axios.post(
                    `${hostNameBack}/api/v1/payments/seller/remove-stripe-account`,
                    { sellerId: user?.sellerId?._id },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setStripeAccountStatus(null);
                enqueueSnackbar("Stripe account removed successfully", { variant: "success" });
            } catch (error) {
                enqueueSnackbar("Failed to remove Stripe account", { variant: "error" });
            }
        }
    };

    function getDateXDaysAgo(days) {
        const d = new Date();
        d.setDate(d.getDate() - days);
        return d;
    }

    const filteredHistory = useMemo(() => {
        if (!history.length) return [];
        if (filter === "lifetime") return history;
        if (filter === "custom") {
            if (!customStartDate || !customEndDate) return history;
            const start = new Date(customStartDate);
            const end = new Date(customEndDate);
            return history.filter((h) => {
                const itemDate = new Date(h.date);
                return itemDate >= start && itemDate <= new Date(end.getTime() + 86399999);
            });
        }
        let rangeDays = 7;
        if (filter === "30d") rangeDays = 30;
        else if (filter === "90d") rangeDays = 90;
        const cutoff = getDateXDaysAgo(rangeDays);
        return history.filter(h => new Date(h.date) >= cutoff);
    }, [history, filter, customStartDate, customEndDate]);

    const isMethodAvailable = (method) => {
        const methodLower = method.toLowerCase();
        return enabledMethods[methodLower] && !restrictions.includes(methodLower);
    };

    const getUnavailableReason = (method) => {
        const methodLower = method.toLowerCase();
        if (!enabledMethods[methodLower]) {
            if (restrictions.includes(methodLower)) return `${method} is currently not available for withdrawals and also restricted in your country.`;
            return `${method} is currently not available for withdrawals.`;
        } else if (restrictions.includes(methodLower)) {
            if (!enabledMethods[methodLower]) return `${method} is currently not available for withdrawals and also restricted in your country.`;
            return `${method} is not available in your country.`;
        }
        return '';
    };

    const allMethods = ['Stripe', 'Wise', 'PayPal'];
    const availableMethods = allMethods.filter(method => isMethodAvailable(method));
    const unavailableMethods = allMethods.filter(method => !isMethodAvailable(method)).map(method => ({
        method,
        reason: getUnavailableReason(method)
    }));

    const paymentElems = filteredHistory.length > 0
        ? filteredHistory.map((item, index) => (
            <div key={index}>
                <div className="requestRow row">
                    <div className="titleField field">
                        <p className="title">{item.description}</p>
                    </div>
                    <p className="idField field">{item.orderIdCustom || "-"}</p>
                    <p className="buyerField field">{item.buyerUsername || "-"}</p>
                    <p className="typeField field">{item.itemType || "-"}</p>
                    <p className="dateField field">{formatDate(item.date)}</p>
                    <p className="methodField field">{item.paymentMethod || "-"}</p>
                    <p className="priceField field">${item.amount}</p>
                </div>
                {filteredHistory.length > 1 && filteredHistory.length - 1 !== index &&
                    <div className="horizontalLine"></div>
                }
            </div>
        ))
        : <div className="row">Nothing to show here...</div>;

const handleGeneratePdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);

    // Determine range label
    let rangeLabel;
    if (filter === "1m") rangeLabel = "Last 1 month";
    else if (filter === "3m") rangeLabel = "Last 3 months";
    else if (filter === "6m") rangeLabel = "Last 6 months";
    else if (filter === "1y") rangeLabel = "Last 1 year";
    else if (filter === "custom" && customStartDate && customEndDate) rangeLabel = `From ${customStartDate} to ${customEndDate}`;
    else rangeLabel = "All Time";

    doc.text(`Account Statement - ${rangeLabel}`, 14, 20);
    doc.setFontSize(12);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 30);

    // Seller details
    doc.setFontSize(14);
    doc.text(`Seller: ${user.sellerId.fullName}`, 14, 40);
    let yPosition = 50;
    if (user.sellerId.companyName) {
        doc.text(`Company: ${user.sellerId.companyName}`, 14, yPosition);
        yPosition += 10;
    }
    if (user.address) {
        doc.text(`Address: ${user.address || ''}, ${user.city || ''}, ${user.zipCode || ''}`, 14, yPosition);
        yPosition += 10;
    }
    if (user.phoneNumber) {
        doc.text(`Phone: ${user.phoneNumber || ''}`, 14, yPosition);
        yPosition += 10;
    }

    // Determine start and end dates
    let startDate, endDate = new Date();
    if (filter === "custom") {
        if (!customStartDate || !customEndDate) {
            enqueueSnackbar("Please select custom dates", { variant: "error" });
            return;
        }
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59, 999); // Include the entire end day
    } else {
        let rangeDays;
        switch (filter) {
            case "1m": rangeDays = 30; break;
            case "3m": rangeDays = 90; break;
            case "6m": rangeDays = 180; break;
            case "1y": rangeDays = 365; break;
            default: rangeDays = 0;
        }
        if (rangeDays > 0) {
            startDate = new Date();
            startDate.setDate(startDate.getDate() - rangeDays);
        } else {
            startDate = new Date(0); // beginning of time
        }
    }

    // Calculate opening balance
    const openingBalance = history
        .filter(h => new Date(h.date) < startDate && (h.description === "Order Completed" || h.description === "Requested for Withdrawal"))
        .reduce((acc, h) => {
            if (h.description === "Order Completed") return acc + h.amount;
            else if (h.description === "Requested for Withdrawal") return acc - h.amount;
            return acc;
        }, 0);

    // Get transactions within the period
    const transactions = history
        .filter(h => new Date(h.date) >= startDate && new Date(h.date) <= endDate && (h.description === "Order Completed" || h.description === "Requested for Withdrawal"))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calculate total earnings and total withdrawals
    const totalEarnings = transactions
        .filter(t => t.description === "Order Completed")
        .reduce((sum, t) => sum + t.amount, 0);
    const totalWithdrawals = transactions
        .filter(t => t.description === "Requested for Withdrawal")
        .reduce((sum, t) => sum + t.amount, 0);

    // Calculate closing balance
    const closingBalance = openingBalance + totalEarnings - totalWithdrawals;

    // Prepare transaction data with running balance
    let balance = openingBalance;
    const transactionData = transactions.map(t => {
        const earnings = t.description === "Order Completed" ? t.amount : 0;
        const withdrawals = t.description === "Requested for Withdrawal" ? t.amount : 0;
        const buyer = t.description === "Order Completed" ? (t.buyerUsername || '-') : '-';
        const paymentMethod = t.paymentMethod || '-';
        if (t.description === "Order Completed") balance += t.amount;
        else if (t.description === "Requested for Withdrawal") balance -= t.amount;
        return [formatDate(t.date), t.description, buyer, paymentMethod, earnings ? `$${earnings.toFixed(2)}` : '', withdrawals ? `$${withdrawals.toFixed(2)}` : '', `$${balance.toFixed(2)}`];
    });

    // Summary table
    const summaryData = [
        ['Opening Balance', `$${openingBalance.toFixed(2)}`],
        ['Total Earnings', `$${totalEarnings.toFixed(2)}`],
        ['Total Withdrawals', `$${totalWithdrawals.toFixed(2)}`],
        ['Closing Balance', `$${closingBalance.toFixed(2)}`]
    ];

    autoTable(doc, {
        startY: yPosition,
        head: [['Summary Item', 'Amount']],
        body: summaryData,
        theme: 'grid',
        styles: { fontSize: 12 },
        columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 60 } },
    });

    // Transaction table
    doc.setFontSize(14);
    doc.text('Transaction History', 14, doc.lastAutoTable.finalY + 10);
    if (transactionData.length === 0) {
        doc.setFontSize(12);
        doc.text('No transactions in this period.', 14, doc.lastAutoTable.finalY + 20);
    } else {
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: [['Date', 'Description', 'Buyer', 'Payment Method', 'Earnings', 'Withdrawals', 'Balance']],
            body: transactionData,
            didParseCell: function (data) {
                if (data.section === 'body') {
                    if (data.column.index === 4 && data.cell.text[0] !== '') { // Earnings column
                        data.cell.styles.textColor = [0, 128, 0]; // green
                    } else if (data.column.index === 5 && data.cell.text[0] !== '') { // Withdrawals column
                        data.cell.styles.textColor = [255, 0, 0]; // red
                    }
                }
            },
        });
    }

    doc.save("account-statement.pdf");
};

    const connectStripe = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${hostNameBack}/api/v1/payments/seller/connect-stripe`,
                { sellerId: user?.sellerId?._id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            window.location.href = response.data.url;
            setLoading(false);
        } catch (error) {
            enqueueSnackbar("Something went wrong!", { variant: "error" });
            setLoading(false);
        }
    };

    const requestedWithdrawal = async () => {
        const activeMethod = stripeActive ? "Stripe" : wiseActive ? "Wise" : paypalActive ? "Paypal" : null;
        if (!activeMethod || !isMethodAvailable(activeMethod)) {
            enqueueSnackbar(`The payment method ${activeMethod || "selected"} is currently unavailable.`, { variant: "error" });
            return;
        }
        setShowWithdrawalModel(false);
        try {
            const response = await axios.post(
                `${hostNameBack}/api/v1/payments/seller/request-withdrawal`,
                {
                    userId: user?._id,
                    sellerId: user?.sellerId?._id,
                    amount: earnings.availableBalance,
                    activeMethod: stripeActive ? "Stripe" : wiseActive ? "Wise" : "Paypal"
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                localStorage.setItem('requestSubmitted', 'true');
                window.location.reload();
            }
        } catch (error) {
            enqueueSnackbar("Something went wrong!", { variant: "error" });
        }
    };

    const addWise = async () => {
        if (!wiseAccountName || !wiseAccountEmail) {
            enqueueSnackbar("Please enter Account ID!", { variant: "error" });
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(
                `${hostNameBack}/api/v1/payments/seller/add-wise`,
                { sellerId: user?.sellerId?._id, wiseAccountName, wiseAccountEmail },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                enqueueSnackbar("Wise account added successfully!", { variant: "success" });
                setShowAddWiseModel(false);
                await updateActivePaymentMethod("Wise");
                await fetchUserData();
            }
            setLoading(false);
        } catch (error) {
            enqueueSnackbar("Something went wrong!", { variant: "error" });
            setLoading(false);
        }
    };

    const addPaypal = async (e) => {
        e.preventDefault();
        if (!paypalEmail) {
            enqueueSnackbar("Please enter Paypal Email!", { variant: "error" });
            return;
        }
        setLoading(true);
        try {
            const response = await axios.post(
                `${hostNameBack}/api/v1/payments/seller/add-paypal`,
                { sellerId: user?.sellerId?._id, paypalEmail },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                enqueueSnackbar("Paypal account added successfully!", { variant: "success" });
                setShowAddPaypalModel(false);
                await updateActivePaymentMethod("Paypal");
                await fetchUserData();
            }
            setLoading(false);
        } catch (error) {
            enqueueSnackbar("Something went wrong!", { variant: "error" });
            setLoading(false);
        }
    };

    const handleToggle = (method) => {
        if (window.confirm(`Are you sure you want to turn off ${method}?`)) {
            updateActivePaymentMethod(null);
        }
    };

    const handleStripeToggle = () => {
        if (!isMethodAvailable("Stripe")) {
            enqueueSnackbar("Stripe is currently unavailable.", { variant: "error" });
            return;
        }
        if (stripeActive) handleToggle("Stripe");
        else updateActivePaymentMethod("Stripe");
    };

    const handleWiseToggle = () => {
        if (!isMethodAvailable("Wise")) {
            enqueueSnackbar("Wise is currently unavailable.", { variant: "error" });
            return;
        }
        if (wiseActive) handleToggle("Wise");
        else updateActivePaymentMethod("Wise");
    };

    const handlePaypalToggle = () => {
        if (!isMethodAvailable("Paypal")) {
            enqueueSnackbar("Paypal is currently unavailable.", { variant: "error" });
            return;
        }
        if (paypalActive) handleToggle("Paypal");
        else updateActivePaymentMethod("Paypal");
    };

    const updateActivePaymentMethod = async (method) => {
        try {
            const response = await axios.post(
                `${hostNameBack}/api/v1/payments/seller/update-active-payment-method`,
                { sellerId: user?.sellerId?._id, activePaymentMethod: method },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                enqueueSnackbar(`Active payment method updated!`, { variant: "success" });
                setStripeActive(method === "Stripe");
                setWiseActive(method === "Wise");
                setPaypalActive(method === "Paypal");
                await fetchUserData();
            }
        } catch (error) {
            enqueueSnackbar("Failed to update active payment method", { variant: "error" });
        }
    };

    return (
        <div className='earningsDiv'>

            {loading && <Loader type="checkmate" />}

            <section className='section'>
                <div className="earningsContent">
                    <div className='sellerPaymentMethodsDiv'>
                        <h2 className='secondaryHeading'><span>Payment</span> Methods</h2>
                        <div className="paymentMethodsContainer">
                            {availableMethods.map(method => (
                                <div key={method} className="paymentMethods">
                                    {method === 'Stripe' && (
                                        <>
                                            <div className="method"><BsStripe className='methodLogo' /><span>Stripe</span></div>
                                            <div className='buttonsDiv'>
                                                <Switch
                                                    checked={user?.sellerId?.activePaymentMethod === "Stripe"}
                                                    onChange={handleStripeToggle}
                                                    color="primary"
                                                    disabled={!user?.sellerId?.stripeAccountId || stripeAccountStatus !== 'complete'}
                                                />
                                                {stripeAccountStatus === 'restricted' || stripeAccountStatus === 'pending' ? (
                                                    <>
                                                        <button
                                                            className='secondaryBtn'
                                                            onClick={connectStripe}
                                                            disabled={loading}
                                                        >
                                                            {loading ? "Loading..." : "Continue Linking"}
                                                        </button>
                                                        <button
                                                            className='secondaryBtn'
                                                            onClick={handleRemoveStripeAccount}
                                                            disabled={loading}
                                                        >
                                                            Remove Incomplete Account
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        className='secondaryBtn'
                                                        onClick={connectStripe}
                                                        disabled={loading || user?.sellerId?.stripeAccountId}
                                                    >
                                                        {user?.sellerId?.stripeAccountId ? "Connected" : loading ? "Loading..." : "Connect"}
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {method === 'Wise' && (
                                        <>
                                            <div className="method"><SiWise className='methodLogo' /><span>Wise</span></div>
                                            <div className='buttonsDiv'>
                                                <Switch
                                                    checked={user?.sellerId?.activePaymentMethod === "Wise"}
                                                    onChange={handleWiseToggle}
                                                    color="primary"
                                                    disabled={!user?.sellerId?.wiseAccountName || !user?.sellerId?.wiseAccountEmail}
                                                />
                                                <button
                                                    className="secondaryBtn"
                                                    onClick={() => setShowAddWiseModel(true)}
                                                    disabled={loading}
                                                >
                                                    {(user?.sellerId?.wiseAccountName || user?.sellerId?.wiseAccountEmail) ? "Edit Details" : "Add Wise"}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                    {method === 'PayPal' && (
                                        <>
                                            <div className="method"><FaPaypal className='methodLogo' /><span>Paypal</span></div>
                                            <div className='buttonsDiv'>
                                                <Switch
                                                    checked={user?.sellerId?.activePaymentMethod === "Paypal"}
                                                    onChange={handlePaypalToggle}
                                                    color="primary"
                                                    disabled={!user?.sellerId?.paypalEmail}
                                                />
                                                <button
                                                    className="secondaryBtn"
                                                    onClick={() => setShowAddPaypalModel(true)}
                                                    disabled={loading}
                                                >
                                                    {user?.sellerId?.paypalEmail ? "Edit Details" : "Add Paypal"}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        {unavailableMethods.length > 0 && (
                            <div className="unavailableMethods">
                                <ul>
                                    {unavailableMethods.map(({ method, reason }) => (
                                        <li key={method} className='infoText' style={{ color: "var(--danger)" }}>{reason}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="earningsOverview">
                        <div className="overviewBox">
                            <h2 className="secondaryHeading">Available Balance</h2>
                            <div className="value">${earnings.availableBalance.toFixed(2)}</div>
                            <button
                                className="secondaryBtn"
                                disabled={Math.floor(earnings.availableBalance) === 0}
                                onClick={() => setShowWithdrawalModel(true)}
                            >
                                Withdraw
                            </button>
                        </div>
                        <div className="overviewBox">
                            <h2 className="secondaryHeading">Total Earnings</h2>
                            <div className="value">${earnings.totalEarnings.toFixed(2)}</div>
                        </div>
                        <div className="overviewBox">
                            <h2 className="secondaryHeading">Paid Balance</h2>
                            <div className="value">${earnings.paidBalance.toFixed(2)}</div>
                        </div>
                        <div className="overviewBox">
                            <h2 className="secondaryHeading">Requested for Withdrawal</h2>
                            <div className="value">${earnings.requestedForWithdrawal.toFixed(2)}</div>
                        </div>
                        <div className="overviewBox">
                            <h2 className="secondaryHeading">Products Sold</h2>
                            <div className="value">
                                {(earnings.productsSold < 10 && "0") + earnings.productsSold}
                            </div>
                        </div>
                        <div className="overviewBox">
                            <h2 className="secondaryHeading">Services Done</h2>
                            <div className="value">
                                {(earnings.servicesDone < 10 && "0") + earnings.servicesDone}
                            </div>
                        </div>
                    </div>

                    <div className="tableDiv paymentHistoryTableDiv">
                        <section className="section">
                            <div className="tableContent">
                                <div className="upper">
                                    <h2 className="secondaryHeading">Payments <span>History</span></h2>
                                    <div className="filterDiv">
                                        {filter === 'custom' && (
                                            <div className="customDateRange">
                                                <label>Start Date:</label>
                                                <input
                                                    type="date"
                                                    value={customStartDate}
                                                    onChange={e => setCustomStartDate(e.target.value)}
                                                    max={today}
                                                />
                                                <label>End Date:</label>
                                                <input
                                                    type="date"
                                                    value={customEndDate}
                                                    onChange={e => setCustomEndDate(e.target.value)}
                                                    max={today}
                                                />
                                            </div>
                                        )}
                                        <select
                                            onChange={e => setFilter(e.target.value)}
                                            className='dropdownPlus'
                                            value={filter}
                                        >
                                            <option value="1m">Last 1 month</option>
                                            <option value="3m">Last 3 months</option>
                                            <option value="6m">Last 6 months</option>
                                            <option value="1y">Last 1 year</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                        <button
                                            className="secondaryBtn generateReportBtn"
                                            onClick={handleGeneratePdf}
                                            disabled={!filteredHistory.length}
                                        >
                                            Generate PDF
                                        </button>
                                    </div>
                                </div>
                                <div className="header">
                                    <p className="title">Activity</p>
                                    <p>Order ID</p>
                                    <p>Buyer</p>
                                    <p>Order Type</p>
                                    <p>Date</p>
                                    <p>Payment Method</p>
                                    <p>Amount</p>
                                </div>
                                <div className="rows">{paymentElems}</div>
                            </div>
                        </section>
                    </div>
                </div>
            </section>

            {showWithdrawalModel && (
                <div className="popupDiv">
                    <div className="popupContent">
                        <div className="form">
                            <h2 className="secondaryHeading">
                                <span>Withdraw</span> Payment
                            </h2>
                            <div className="horizontalLine"></div>
                            <div className="description">
                                {(stripeActive && user?.sellerId?.stripeAccountId) ? earnings.availableBalance >= 30 ?
                                    <div className='form'>
                                        <div>Active Method: <span className='fw500'>Stripe</span></div>
                                        <p>You are about to withdraw ${earnings.availableBalance}.</p>
                                    </div> :
                                    <p>Your available balance is less than $30.</p> :
                                    (wiseActive && (user?.sellerId?.wiseAccountName || user?.sellerId?.wiseAccountEmail)) ? earnings.availableBalance >= 30 ?
                                        <div className='form'>
                                            <div>Active Method: <span className='fw500'>Wise</span></div>
                                            <p>You are about to withdraw ${earnings.availableBalance}.</p>
                                        </div> :
                                        <p>Your available balance is less than $30.</p> :
                                        (paypalActive && user?.sellerId?.paypalEmail) ? earnings.availableBalance >= 30 ?
                                            <div className='form'>
                                                <div>Active Method: <span className='fw500'>Paypal</span></div>
                                                <p>You are about to withdraw ${earnings.availableBalance}.</p>
                                            </div> :
                                            <p>Your available balance is less than $30.</p> :
                                            <p>No active payment method.</p>
                                }
                            </div>
                            <div className="buttonsDiv">
                                <button
                                    className="primaryBtn"
                                    type="button"
                                    disabled={
                                        (!user?.sellerId?.stripeAccountId &&
                                            !user?.sellerId?.wiseAccountName &&
                                            !user?.sellerId?.wiseAccountEmail &&
                                            !user?.sellerId?.paypalEmail) ||
                                        (earnings.availableBalance < 30) ||
                                        (!stripeActive && !wiseActive && !paypalActive)
                                    }
                                    onClick={requestedWithdrawal}
                                >
                                    Withdraw ${earnings.availableBalance}
                                </button>
                                <button
                                    className="secondaryBtn"
                                    type="button"
                                    onClick={() => setShowWithdrawalModel(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline
                            className="icon"
                            onClick={() => setShowWithdrawalModel(false)}
                        />
                    </div>
                </div>
            )}

            {showAddWiseModel && (
                <div className="popupDiv">
                    <div className="popupContent">
                        <div className="form">
                            <h2 className="secondaryHeading">Add <span>Wise</span></h2>
                            <div className="horizontalLine"></div>
                            <div className="inputDiv">
                                <label>Your Wise Account Name</label>
                                <input
                                    type="text"
                                    value={wiseAccountName}
                                    onChange={(e) => setWiseAccountName(e.target.value)}
                                    className='inputField'
                                    placeholder="Enter the name on your Wise Account"
                                />
                            </div>
                            <div className="inputDiv">
                                <label>Wise Email</label>
                                <input
                                    type="text"
                                    value={wiseAccountEmail}
                                    onChange={(e) => setWiseAccountEmail(e.target.value)}
                                    className='inputField'
                                    placeholder="Enter your Wise Email"
                                />
                            </div>
                            <p>Make sure you enter correct Account Name and Email, otherwise you may not receive your funds!</p>
                            <div className="buttonsDiv">
                                <button className="primaryBtn" type="button" onClick={addWise}>Save</button>
                                <button className="secondaryBtn" type="button" onClick={() => setShowAddWiseModel(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowAddWiseModel(false)} />
                    </div>
                </div>
            )}

            {showAddPaypalModel && (
                <div className="popupDiv">
                    <div className="popupContent">
                        <form className="form" onSubmit={addPaypal}>
                            <h2 className="secondaryHeading">Add <span>Paypal</span></h2>
                            <div className="horizontalLine"></div>
                            <div className="inputDiv">
                                <label>Paypal Email</label>
                                <input
                                    type="email"
                                    value={paypalEmail}
                                    onChange={(e) => setPaypalEmail(e.target.value)}
                                    className='inputField'
                                    placeholder="Enter your Paypal Email"
                                    required
                                />
                            </div>
                            <p>Make sure you enter correct Paypal Email.</p>
                            <div className="buttonsDiv">
                                <button className="primaryBtn" type="submit">Save</button>
                                <button
                                    className="secondaryBtn"
                                    type="button"
                                    onClick={() => setShowAddPaypalModel(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline
                            className="icon"
                            onClick={() => setShowAddPaypalModel(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SellerEarnings;